import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, CreditCard, Gift, Mail } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

export default function GiftCards() {
  const navigate = useNavigate();
  const [selectedGift, setSelectedGift] = useState({ name: 'Group Lesson', price: 400 });
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [loading, setLoading] = useState(false);

  const gifts = [
    { name: 'Group Lesson', price: 400, description: 'A shared trip for up to 4 hours for your group.' },
    { name: 'Team Building', price: 1500, description: 'A corporate session for the group, up to 4 hours. All equipment provided.' },
    { name: 'The Syndicate', price: 500, description: 'Syndicate Initiation. Includes Lake Ontario crossings and Baja path access.' },
  ];

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      try {
        const response = await fetch('/api/create-gift-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            giftName: selectedGift.name,
            price: selectedGift.price,
            recipientName,
            recipientEmail,
            senderName: senderName || 'Website Guest'
          }),
        });

        if (!response.ok) throw new Error('API not available');

        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } catch (error: any) {
        console.error("Stripe Session Creation Failed:", error);
        throw new Error(error.message || "Failed to initiate payment. Please contact syndicate command.");
      }
    } catch (error: any) {
      console.error("Booking error:", error);
      alert(`Terminal Offline: ${error.message || 'Transaction failed.'}\n\nPlease configure environment variables if running in preview.`);
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Gift Cards | Open Water Syndicate</title>
        <meta name="description" content="Give the gift of the open water. Purchase digital gift cards for paddle board coaching, team building, or Ultimate Membership." />
        <link rel="canonical" href="https://openwatersyndicate.com/gift" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="h-16 w-16 bg-ows-teal flex items-center justify-center rounded-2xl mx-auto mb-8 text-white">
            <Gift className="h-8 w-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
            Gift Cards
          </h1>
          <p className="text-lg text-ows-dark/60 max-w-2xl mx-auto font-light leading-relaxed">
            A digital code with pure utility. Let them redeem it when the wind is right.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-5xl mx-auto">
          {/* Form */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.4em] text-ows-dark mb-6">Select Amount</h3>
              <div className="grid grid-cols-1 gap-4">
                {gifts.map((gift) => (
                  <label 
                    key={gift.name}
                    className={`cursor-pointer border rounded-[2rem] p-6 flex flex-col gap-2 transition-all ${selectedGift.name === gift.name ? 'border-ows-teal bg-ows-sand/20' : 'border-ows-teal/10 bg-white hover:border-ows-teal/30'}`}
                    onClick={() => setSelectedGift(gift)}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold tracking-widest uppercase text-ows-dark text-sm">{gift.name}</span>
                      <span className="font-display text-xl">${gift.price}</span>
                    </div>
                    <span className="text-xs text-ows-dark/60 font-light">{gift.description}</span>
                  </label>
                ))}
              </div>
            </div>

            <form onSubmit={handleCheckout} className="space-y-6">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 mb-2 flex items-center gap-2">
                  <Mail className="h-3 w-3" /> Recipient Details
                </label>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Recipient Name" 
                    required 
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    className="w-full px-6 py-4 bg-white border border-ows-teal/10 rounded-2xl text-ows-dark outline-none focus:border-ows-teal transition-colors" 
                  />
                  <input 
                    type="email" 
                    placeholder="Recipient Email" 
                    required 
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="w-full px-6 py-4 bg-white border border-ows-teal/10 rounded-2xl text-ows-dark outline-none focus:border-ows-teal transition-colors" 
                  />
                  <input 
                    type="text" 
                    placeholder="Sender Name (Your Name)" 
                    required 
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    className="w-full px-6 py-4 bg-white border border-ows-teal/10 rounded-2xl text-ows-dark outline-none focus:border-ows-teal transition-colors" 
                  />
                </div>
              </div>
              
              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-5 bg-ows-dark text-white font-bold tracking-[0.2em] uppercase text-xs hover:bg-ows-teal transition-all rounded-full flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? 'Processing...' : `Purchase Gift Card — $${selectedGift.price}`} 
                <CreditCard className="h-4 w-4" />
              </button>
            </form>
          </motion.div>

          {/* Preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center justify-center"
          >
            <div className="bg-ows-dark w-full max-w-sm aspect-[1.58/1] rounded-3xl p-8 flex flex-col justify-between relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-ows-teal/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
              
              <div className="flex justify-between items-start z-10">
                <div className="text-white">
                  <div className="text-[10px] font-bold tracking-[0.4em] uppercase text-white/40 mb-1">Gift Card</div>
                  <div className="text-xl font-bold tracking-widest uppercase">{selectedGift.name}</div>
                </div>
                <div className="text-ows-teal font-display text-3xl">
                  ${selectedGift.price}
                </div>
              </div>
              
              <div className="z-10">
                <div className="text-[10px] font-bold tracking-[0.4em] uppercase text-white/40 mb-1">To</div>
                <div className="text-white font-serif italic text-lg">{recipientName || 'Name'}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mt-4">Code will be emailed to {recipientEmail || 'address'}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
